import {
  AdminApiError,
  type AdminClient,
  type AuditEntry,
} from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { fmtDateTime, fmtRelative } from '../format.js';
import { icon, type IconName } from '../icons.js';
import { skeletonTable } from '../skeleton.js';

export interface ActivityPageDeps {
  client: AdminClient;
  integratorId: string;
}

interface ActionMeta {
  icon: IconName;
  verb: string;
  color?: string;
}

const ACTION_META: Array<{ match: RegExp; meta: ActionMeta }> = [
  { match: /^integrator\./, meta: { icon: 'building', verb: 'updated integrator' } },
  { match: /^player\.balance\.adjust/, meta: { icon: 'coin', verb: 'adjusted balance' } },
  { match: /^player\.ban/, meta: { icon: 'ban', verb: 'banned player', color: 'var(--warn)' } },
  { match: /^player\./, meta: { icon: 'user', verb: 'updated player' } },
  { match: /^bet\.refund/, meta: { icon: 'coin', verb: 'refunded bet', color: 'var(--accent-2)' } },
  { match: /^round\.force_finish/, meta: { icon: 'shield', verb: 'force-finished round', color: 'var(--warn)' } },
  { match: /^round\.manual_start/, meta: { icon: 'sparkle', verb: 'started round' } },
  { match: /^admin_user\.create/, meta: { icon: 'user-plus', verb: 'created admin' } },
  { match: /^admin_user\.grant_role/, meta: { icon: 'lock', verb: 'granted role' } },
  { match: /^admin_user\.revoke_role/, meta: { icon: 'unlock', verb: 'revoked role' } },
  { match: /^admin_user\./, meta: { icon: 'lock', verb: 'updated admin' } },
  { match: /^game_config\.|^tenant_config_approval/, meta: { icon: 'settings', verb: 'changed game config' } },
  { match: /^webhook\./, meta: { icon: 'webhook', verb: 'updated webhook' } },
  { match: /^api_key\./, meta: { icon: 'key', verb: 'updated API key' } },
];

function metaFor(action: string): ActionMeta {
  for (const m of ACTION_META) if (m.match.test(action)) return m.meta;
  return { icon: 'edit', verb: action };
}

const timeAgo = fmtRelative;

function initialsFor(email: string): string {
  const part = email.split('@')[0] ?? email;
  return part.slice(0, 2).toUpperCase();
}

export async function renderActivityPage(
  root: HTMLElement,
  deps: ActivityPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Activity feed</h1>
    <p class="page-sub">Friendlier view of <a href="/audit" style="color:var(--accent-2)">the audit log</a>. Filter by actor, action or target; click an entry to expand the raw before/after diff.</p>

    <div class="card">
      <div class="row act-filters" style="margin-top:0;gap:10px;flex-wrap:wrap;align-items:center">
        <input id="act-filter" type="text" placeholder="Filter by actor email…" class="act-input" />
        <select id="act-action" class="act-select" title="Filter by action category">
          <option value="">All actions</option>
        </select>
        <select id="act-target" class="act-select" title="Filter by target type">
          <option value="">All targets</option>
        </select>
        <span id="act-count" class="muted" style="font-size:12px"></span>
        <button id="act-refresh" class="primary" style="margin-left:auto">Refresh</button>
      </div>
    </div>

    <div class="card">
      <div id="act-list">${skeletonTable({ rows: 6, columns: 3 })}</div>
    </div>
  `;
  const listEl = root.querySelector<HTMLElement>('#act-list')!;
  const filterEl = root.querySelector<HTMLInputElement>('#act-filter')!;
  const actionEl = root.querySelector<HTMLSelectElement>('#act-action')!;
  const targetEl = root.querySelector<HTMLSelectElement>('#act-target')!;
  const countEl = root.querySelector<HTMLElement>('#act-count')!;
  let entries: AuditEntry[] = [];

  /** Repopulate the action + target dropdowns from the loaded entries,
   *  preserving the current selection. */
  function populateFilters(): void {
    const fill = (sel: HTMLSelectElement, values: string[], allLabel: string): void => {
      const cur = sel.value;
      sel.innerHTML =
        `<option value="">${allLabel}</option>` +
        values.map((v) => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`).join('');
      sel.value = values.includes(cur) ? cur : '';
    };
    const actionCats = [...new Set(entries.map((e) => e.action.split('.')[0] ?? e.action))].sort();
    const targetTypes = [...new Set(entries.map((e) => e.targetType))].sort();
    fill(actionEl, actionCats, 'All actions');
    fill(targetEl, targetTypes, 'All targets');
  }

  function render(): void {
    const f = filterEl.value.trim().toLowerCase();
    const act = actionEl.value;
    const tgt = targetEl.value;
    const visible = entries.filter(
      (e) =>
        (!f || e.actorEmail.toLowerCase().includes(f)) &&
        (!act || (e.action.split('.')[0] ?? e.action) === act) &&
        (!tgt || e.targetType === tgt),
    );
    countEl.textContent = `${visible.length} of ${entries.length}`;
    if (visible.length === 0) {
      listEl.innerHTML = emptyState({
        icon: 'sparkle',
        title: 'No matching activity',
        hint: entries.length === 0 ? 'Audit log is empty.' : 'No entries match the current filters.',
      });
      return;
    }
    listEl.innerHTML = `<ul style="list-style:none;padding:0;margin:0">
      ${visible.map(itemHtml).join('')}
    </ul>`;
  }

  function itemHtml(e: AuditEntry): string {
    const m = metaFor(e.action);
    const initials = initialsFor(e.actorEmail);
    const diff = JSON.stringify({ before: e.before, after: e.after }, null, 2);
    const iconColor = m.color ?? 'var(--accent-2)';
    return `<li style="border-bottom:1px solid var(--border)">
      <details class="act-item" style="padding:12px 8px">
        <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;gap:12px">
          <div style="width:32px;height:32px;border-radius:50%;background:var(--panel-2);display:grid;place-items:center;font-size:11px;font-weight:600;color:var(--text-dim)">${initials}</div>
          <div style="width:32px;height:32px;border-radius:50%;background:var(--panel-2);display:grid;place-items:center;color:${iconColor}">${icon(m.icon, { size: 16 })}</div>
          <div style="flex:1;font-size:13px">
            <div><strong>${escapeHtml(e.actorEmail)}</strong> <span style="color:${m.color ?? 'var(--text-dim)'}">${escapeHtml(m.verb)}</span> <code style="font-size:11px">${escapeHtml(e.targetType)}</code> <code style="font-size:11px" title="${escapeHtml(e.targetId)}">${escapeHtml(prettyTargetId(e.targetId))}</code></div>
            <div class="muted" style="font-size:11px;margin-top:2px">${escapeHtml(e.action)} · ${timeAgo(e.createdAt)} · ${fmtDateTime(e.createdAt)}</div>
          </div>
          <div style="color:var(--text-dim);display:flex;align-items:center">${chevronDown()}</div>
        </summary>
        <pre class="diff act-diff-loaded" style="margin:10px 0 0;max-height:300px;overflow:auto;font-size:11px">${escapeHtml(diff)}</pre>
      </details>
    </li>`;
  }

  function chevronDown(): string {
    return '<svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>';
  }

  async function refresh(): Promise<void> {
    listEl.innerHTML = skeletonTable({ rows: 6, columns: 3 });
    try {
      const res = await deps.client.listAuditLog({ integratorId: deps.integratorId, limit: 100 });
      entries = res.entries;
      populateFilters();
      render();
    } catch (err) {
      listEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
    }
  }

  filterEl.addEventListener('input', () => render());
  actionEl.addEventListener('change', () => render());
  targetEl.addEventListener('change', () => render());
  root.querySelector<HTMLButtonElement>('#act-refresh')!.addEventListener('click', () => void refresh());

  await refresh();
}

/** Human-friendly target id. Composite ids ("<uuid>:greedyking") show their
 *  trailing segment; bare uuids show a clearly-truncated "…last8". */
function prettyTargetId(id: string): string {
  if (id.includes(':')) {
    const tail = id.split(':').pop() ?? id;
    return tail || id;
  }
  return id.length > 14 ? `…${id.slice(-8)}` : id;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
